import { StateService, UIRouterGlobals } from "@uirouter/angular";
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { Subscription, forkJoin, take } from "rxjs";
import { IComment } from "@gtmhub/comments";
import { ProgressColoring } from "@gtmhub/configuration/services/progress-coloring";
import dayjs from "@gtmhub/dayjs";
import { IIndicatorMap } from "@gtmhub/error-handling";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { CommentsFacade } from "@webapp/comments/services/comments-facade.service";
import { ReactionsFacade } from "@webapp/comments/services/reactions-facade.service";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { GoalsFacade } from "@webapp/okrs/goals/services/goals-facade.service";
import { IMetricSnapshot } from "@webapp/okrs/metrics/models/metric.models";
import { MetricsFacade } from "@webapp/okrs/metrics/services/metrics-facade.service";

@Component({
  selector: "thread",
  templateUrl: "thread.component.html",
  styleUrls: ["thread.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestroyService],
})
export class ThreadComponent implements OnInit, OnDestroy {
  public indicators: IIndicatorMap = {
    getItem: { progress: true },
  };

  public params;
  public metric;
  public metadata;
  public commentTargetTitle;
  public snapshotReactions;
  public commentsCount;
  public commentMentions;
  public metricSnapshot: IMetricSnapshot;
  public featureAvailable: (feature: string) => boolean;
  private subscriptions: Subscription[] = [];

  constructor(
    private stateService: StateService,
    private broadcastService: BroadcastService,
    private goalsFacade: GoalsFacade,
    private metricsFacade: MetricsFacade,
    private commentsFacade: CommentsFacade,
    private reactionsFacade: ReactionsFacade,
    private progressColoring: ProgressColoring,
    private routerGlobals: UIRouterGlobals,
    @Inject("$rootScope") private rootScope: IGtmhubRootScopeService
  ) {
    this.indicators = {};
    this.params = this.routerGlobals.params;

    this.subscriptions.push(
      this.broadcastService.on("commentPosted").subscribe((comment: IComment) => {
        this.onCommentPosted(comment);
      })
    );

    this.subscriptions.push(
      this.broadcastService.on("commentDeleted").subscribe((comment: IComment) => {
        this.onCommentDeleted(comment);
      })
    );
  }

  public ngOnInit(): void {
    this.featureAvailable = (feature: string): boolean => this.rootScope.featureAvailable(feature);
    if (this.params.targetType === "checkin") {
      this.getMetric();
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  public onCancel(): void {
    this.stateService.go("^");
  }

  private getMetric(): void {
    this.indicators.getItem = { progress: true };

    const metricByCheckin$ = this.metricsFacade.getMetricByCheckin$(this.params.targetId);
    const reactions$ = this.reactionsFacade.getReactions$({ targetIds: this.params.targetId });
    const comments$ = this.commentsFacade.getComments$({}, { targetType: "checkin", targetIds: this.params.targetId });

    forkJoin([metricByCheckin$, reactions$, comments$])
      .pipe(take(1))
      .subscribe({
        next: ([metric, reactions, comments]) => {
          this.metric = metric;
          this.metadata = {
            metricId: this.metric.id,
          };
          this.commentTargetTitle = this.metric.name;

          for (let i = 0; i < this.metric.metricHistory.length; i++) {
            if (this.metric.metricHistory[i].id && this.metric.metricHistory[i].id === this.params.targetId) {
              this.metricSnapshot = this.metric.metricHistory[i];

              if (dayjs(this.metric.metricHistory[i].updatedAt).format("YYYY-MM-DD") !== dayjs(this.metric.metricHistory[i].createdAt).format("YYYY-MM-DD")) {
                this.metricSnapshot.updatedLate = true;
              }

              this.metricSnapshot.view = {
                color: this.progressColoring.getColorOrGeneric(this.metric.metricHistory[i].currentAttainment),
              };
            }
          }

          this.snapshotReactions = reactions;
          this.commentsCount = comments.length;

          delete this.indicators.getItem;

          this.goalsFacade
            .getGoal$(this.metric.goalId)
            .pipe(take(1))
            .subscribe((goal) => {
              const mentions = Array.isArray(this.metricSnapshot?.mentions?.mentioned?.comment) ? this.metricSnapshot.mentions.mentioned.comment : [];
              this.commentMentions = [...this.metric.ownerIds, ...goal.ownerIds, ...mentions];
            });
        },
        error: (error) => {
          this.indicators.getItem = { error };
        },
      });
  }

  private onCommentPosted(comment: IComment): void {
    if (this.params.targetId === comment.targetId) {
      this.commentsCount++;
    }
  }

  private onCommentDeleted(comment: IComment): void {
    if (this.params.targetId === comment.targetId && this.commentsCount > 0) {
      this.commentsCount--;
    }
    this.stateService.go("^");
  }
}
